import { useCallback } from 'react';
import { Stack, Text } from '@adc/parallax-component-library';
import { differenceInYears, format, parseISO } from 'date-fns';
import { type ConfigState } from 'Reducers/config/types';
import { CLOSE_MODAL, SHOW_MODAL } from 'Reducers/modal/types';
import i18n, { type TFuncKey } from 'Utilities/i18n';
import { store } from 'Utilities/store';

type EntryItem = {
  value: string;
  msgKey: TFuncKey;
  count?: number;
};

type ValidationType = Pick<ConfigState, 'validation'>;

const translationsKeysData = {
  createAccount: {
    differentPasswordTitleKey:
      'CreateAccountAccountInfo.modals.accountInfoErrors.differentPasswordTitle',
    differentPasswordSubtitleKey: '',
  },
  updatePassword: {
    differentPasswordTitleKey:
      'ChangePassword.modals.changePasswordErrors.reusedPasswordError.title',
    differentPasswordSubtitleKey:
      'ChangePassword.modals.changePasswordErrors.reusedPasswordError.message',
  },
} as const;

const useAccount = (type?: 'createAccount' | 'updatePassword') => {
  const translationKeys = translationsKeysData[type ?? 'createAccount'];

  const showEmailExistsMessage = useCallback((onClickSignIn: () => void) => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        testID: 'CreateAccountAccountInfo.modals.accountInfoErrors.emailExists',
        details: (
          <Stack gap="$4">
            <Text>
              {i18n.t('CreateAccountAccountInfo.modals.accountInfoErrors.emailExists', {
                appName: i18n.t('Global.appNames.lactateView'),
              })}
            </Text>
            <Text>{i18n.t('CreateAccountAccountInfo.modals.accountInfoErrors.trySignIn')}</Text>
          </Stack>
        ),
        primaryAction: {
          label: i18n.t('CreateAccountAccountInfo.modals.accountInfoErrors.signIn'),
          onPress: () => {
            store.dispatch({
              type: CLOSE_MODAL,
            });
            onClickSignIn();
          },
        },
        secondaryAction: {
          label: i18n.t('CreateAccountAccountInfo.modals.accountInfoErrors.cancel'),
          onPress: () => {
            store.dispatch({
              type: CLOSE_MODAL,
            });
          },
        },
        buttonLayout: 'column',
      },
    });
  }, []);

  const showExistingProUserError = useCallback(() => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        testID: 'CreateAccountAccountInfo.modals.accountInfoErrors.emailExistsProfessional',
        details: i18n.t(
          'CreateAccountAccountInfo.modals.accountInfoErrors.emailExistsProfessional',
          { appName: i18n.t('Global.appNames.lactateView') }
        ),
      },
    });
  }, []);

  const validateEntriesLengthAndDisplayError = (items: EntryItem[]) => {
    const messages: { msgKey: TFuncKey; count: number }[] = [];

    for (const item of items) {
      if (item.count && item.value.length > item.count) {
        messages.push({ msgKey: item.msgKey, count: item.count });
      }
    }

    if (messages.length) {
      store.dispatch({
        type: SHOW_MODAL,
        modalProps: {
          testID: 'CreateAccountAccountInfo.modals.accountInfoErrors.validationError',
          details: (
            <Stack gap="$4">
              {messages?.map((message, i) => (
                <Text key={i} testID={message.msgKey} type="$bodyBase.default">
                  {i18n.t(message.msgKey, { count: message.count })}
                </Text>
              ))}
            </Stack>
          ),
        },
      });

      return false;
    }

    return true;
  };

  const showPasswordValidationError = useCallback(
    ({ validation }: ValidationType, errors?: { error: string; key: string }[]) => {
      store.dispatch({
        type: SHOW_MODAL,
        modalProps: {
          testID: 'CreateAccountAccountInfo.modals.accountInfoErrors.securityRules',
          details: (
            <Stack gap="$4">
              <Text>{i18n.t(translationKeys.differentPasswordTitleKey)}</Text>

              {translationKeys.differentPasswordSubtitleKey !== '' ? (
                <Text>{i18n.t(translationKeys.differentPasswordSubtitleKey)}</Text>
              ) : null}

              {errors?.map((e) => {
                const limit: number | undefined =
                  e.error === 'max'
                    ? Number(
                        validation?.identity.find((item) => item.data.key === e.error)?.data.value
                      )
                    : Number(
                        validation?.identity.find((item) => item.type === e.error)?.data.limit
                      );

                return (
                  <Text key={e.error}>
                    {i18n.t(
                      `CreateAccountAccountInfo.modals.accountInfoErrors.securityRules.${e.error as 'context'}`,
                      {
                        count: Number.isNaN(limit) ? 0 : limit,
                      }
                    )}
                  </Text>
                );
              })}
            </Stack>
          ),
        },
      });
    },
    [translationKeys.differentPasswordSubtitleKey, translationKeys.differentPasswordTitleKey]
  );

  const validateIsMinor = useCallback((dateOfBirth: string, adultAge: number) => {
    const dob = parseISO(dateOfBirth);
    const age = differenceInYears(new Date(), dob);

    return age < adultAge;
  }, []);

  const validateDOBAndDisplayError = useCallback((dateOfBirth: string) => {
    const today = format(new Date(), 'yyyy-MM-dd');

    if (dateOfBirth > today) {
      store.dispatch({
        type: SHOW_MODAL,
        modalProps: {
          testID: 'CreateAccountPersonalInfo.modals.personalInfoErrors.dobError',
          details: i18n.t('CreateAccountPersonalInfo.modals.personalInfoErrors.dobError'),
        },
      });

      return false;
    }

    return true;
  }, []);

  const showMinorRestrictionMessage = useCallback((adultAge: number) => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        testID: 'CreateAccountPersonalInfo.modals.personalInfoErrors.countryInvalidAge',
        details: i18n.t('CreateAccountPersonalInfo.modals.personalInfoErrors.countryInvalidAge', {
          age: adultAge,
        }),
      },
    });
  }, []);

  return {
    showEmailExistsMessage,
    showExistingProUserError,
    showPasswordValidationError,
    showMinorRestrictionMessage,
    validateEntriesLengthAndDisplayError,
    validateIsMinor,
    validateDOBAndDisplayError,
  };
};

export default useAccount;
